// Enhanced AdminBilling.jsx with green theme matching AdminPackages
import React, { useState, useEffect } from 'react';
import SidebarHeader from '../../components/admin/sidebar';
import { useAuth } from '../../pages/auth/AuthContext';
import RequireAdminRole from '../../components/auth/userole';
import { 
  Search, 
  Filter, 
  RefreshCw, 
  Eye, 
  Edit, 
  Trash2,
  DollarSign,
  FileText,
  Calendar,
  User,
  CheckCircle,
  XCircle,
  AlertCircle,
  Clock,
  ChevronDown,
  ChevronUp,
  Download,
  CreditCard
} from 'lucide-react';

// API configuration
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';

const STATUS_OPTIONS = [
  { value: '', label: 'Semua Status' },
  { value: 'pending', label: 'Pending' },
  { value: 'paid', label: 'Lunas' },
  { value: 'overdue', label: 'Terlambat' },
  { value: 'partial', label: 'Sebagian' },
  { value: 'failed', label: 'Gagal' }
];

const PERIOD_OPTIONS = [
  { value: '', label: 'Semua Period' },
  { value: 'monthly', label: 'Bulanan' },
  { value: 'quarterly', label: 'Triwulan' },
  { value: 'yearly', label: 'Tahunan' }
];

const AdminBillingPage = () => {
  const { user, loading } = useAuth();
  const { user: contextUser, token: contextToken } = useAuth();
  
  const [activeTab, setActiveTab] = useState('billings');
  const [billings, setBillings] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [periodFilter, setPeriodFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [currentUser, setCurrentUser] = useState({
    name: contextUser?.fullName || contextUser?.name || 'Admin User',
    email: contextUser?.email || 'admin@telnet.co.id',
    role: contextUser?.role || 'Super Admin'
  });

  // Modal states
  const [selectedBilling, setSelectedBilling] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Form states for update
  const [billingForm, setBillingForm] = useState({
    status: '',
    paymentDate: '',
    paymentMethod: '',
    notes: ''
  });

  const LIMIT = 10;

  // Enhanced token handling
  const authToken = contextToken ||
                    localStorage.getItem('adminToken') ||
                    localStorage.getItem('token') ||
                    sessionStorage.getItem('adminToken') ||
                    sessionStorage.getItem('token');

  // Enhanced auth headers function
  const getAuthHeaders = () => {
    const headers = {
      'Content-Type': 'application/json'
    };
    
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }
    
    return headers;
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatCurrencyShort = (amount) => {
    if (amount >= 1000000000) {
      return `Rp ${(amount / 1000000000).toFixed(1)}B`;
    } else if (amount >= 1000000) {
      return `Rp ${(amount / 1000000).toFixed(1)}M`;
    } else if (amount >= 1000) {
      return `Rp ${(amount / 1000).toFixed(1)}K`;
    }
    return `Rp ${Math.round(amount).toLocaleString('id-ID')}`;
  };

  // Format tanggal
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('id-ID', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('id-ID', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get status color and label
  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      paid: 'bg-green-100 text-green-800 border-green-200',
      overdue: 'bg-red-100 text-red-800 border-red-200',
      partial: 'bg-orange-100 text-orange-800 border-orange-200',
      failed: 'bg-red-200 text-red-900 border-red-300'
    };
    return colors[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: <Clock size={14} className="text-yellow-600" />,
      paid: <CheckCircle size={14} className="text-green-600" />,
      overdue: <AlertCircle size={14} className="text-red-600" />,
      partial: <AlertCircle size={14} className="text-orange-600" />,
      failed: <XCircle size={14} className="text-red-600" />
    };
    return icons[status] || <Clock size={14} className="text-gray-600" />;
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'Pending',
      paid: 'Lunas',
      overdue: 'Terlambat',
      partial: 'Sebagian',
      failed: 'Gagal'
    };
    return labels[status] || status;
  };

  // Enhanced fetch billings with better error handling
  const fetchBillings = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const params = new URLSearchParams({
        page: page.toString(),
        limit: LIMIT.toString(),
        ...(statusFilter && { status: statusFilter }),
        ...(periodFilter && { period: periodFilter }),
        ...(search && { search })
      });

      const headers = getAuthHeaders();
      const adminBillingsUrl = `${API_BASE_URL}/api/billings/all?${params.toString()}`;

      const response = await fetch(adminBillingsUrl, {
        method: 'GET',
        headers,
        credentials: authToken ? 'include' : 'omit'
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Session expired. Please login again.');
        } else if (response.status === 403) {
          throw new Error('You do not have permission to view billings.');
        } else if (response.status === 404) {
          throw new Error('Billings endpoint not found. Please check your API configuration.');
        } else {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || `HTTP ${response.status}: Failed to fetch billings`);
        }
      }

      const data = await response.json();

      if (data.success) {
        const billingsData = Array.isArray(data.data) ? data.data : [];
        setBillings(billingsData);
        setTotalPages(data.pagination?.totalPages || Math.ceil(billingsData.length / LIMIT) || 1);
        setError(null);
      } else {
        throw new Error(data.message || 'Failed to load billings');
      }
    } catch (error) {
      console.error('Error fetching billings:', error);
      
      let errorMessage = error.message;
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        errorMessage = 'Network error. Please check your connection and try again.';
      }
      
      setBillings([]);
      setTotalPages(1);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Update billing status
  const handleUpdateBilling = async () => {
    try {
      if (!selectedBilling) return;
      
      const response = await fetch(`${API_BASE_URL}/api/billings/${selectedBilling._id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        credentials: authToken ? 'include' : 'omit',
        body: JSON.stringify({
          'payment.status': billingForm.status,
          'payment.paidAt': billingForm.paymentDate,
          'payment.method': billingForm.paymentMethod,
          'billing.notes': billingForm.notes
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: Failed to update billing`);
      }

      const data = await response.json();
      
      if (data.success) {
        alert('Billing berhasil diupdate!');
        setShowUpdateModal(false);
        setSelectedBilling(null);
        resetBillingForm();
        fetchBillings();
      } else {
        throw new Error(data.message || 'Failed to update billing');
      }
    } catch (error) {
      console.error('Error updating billing:', error);
      alert('Error updating billing: ' + error.message);
    }
  };

  // Delete billing
  const handleDeleteBilling = async () => {
    try {
      if (!selectedBilling) return;

      const response = await fetch(`${API_BASE_URL}/api/billings/${selectedBilling._id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
        credentials: authToken ? 'include' : 'omit'
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: Failed to delete billing`);
      }

      const data = await response.json();
      
      if (data.success) {
        alert('Billing berhasil dihapus!');
        setShowDeleteModal(false);
        setSelectedBilling(null);
        fetchBillings();
      } else {
        throw new Error(data.message || 'Failed to delete billing');
      }
    } catch (error) {
      console.error('Error deleting billing:', error);
      alert('Error deleting billing: ' + error.message);
    }
  };

  // Reset billing form
  const resetBillingForm = () => {
    setBillingForm({
      status: '',
      paymentDate: '',
      paymentMethod: '',
      notes: ''
    });
  };

  // Open modals
  const openDetailModal = (billing) => {
    setSelectedBilling(billing);
    setShowDetailModal(true);
  };

  const openUpdateModal = (billing) => {
    setSelectedBilling(billing);
    setBillingForm({
      status: billing.payment?.status || '',
      paymentDate: billing.payment?.paidAt ? new Date(billing.payment.paidAt).toISOString().split('T')[0] : '',
      paymentMethod: billing.payment?.method || '',
      notes: billing.billing?.notes || ''
    });
    setShowUpdateModal(true);
  };

  const openDeleteModal = (billing) => {
    setSelectedBilling(billing);
    setShowDeleteModal(true);
  };

  // Update current user when context changes
  useEffect(() => {
    if (contextUser) {
      setCurrentUser({
        name: contextUser.fullName || contextUser.name || 'Admin User',
        email: contextUser.email || 'admin@telnet.co.id',
        role: contextUser.role || 'Super Admin'
      });
    }
  }, [contextUser]);

  useEffect(() => {
    fetchBillings();
  }, [page, statusFilter, periodFilter, authToken]);

  // Close sidebar when clicking outside on mobile
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    fetchBillings();
  };

  const goToPage = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const handleRetry = () => {
    fetchBillings();
  };

  // Stats Cards Component with green theme
  const StatsCards = () => {
    const stats = {
      total: billings.length,
      paid: billings.filter(billing => billing.payment?.status === 'paid').length,
      pending: billings.filter(billing => billing.payment?.status === 'pending').length,
      revenue: billings
        .filter(billing => billing.payment?.status === 'paid')
        .reduce((sum, billing) => sum + (billing.pricing?.totalAmount || 0), 0)
    };

    return (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-6">
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Billings</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <FileText className="text-blue-500" size={32} />
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Paid</p>
              <p className="text-2xl font-bold text-gray-900">{stats.paid}</p>
            </div>
            <CheckCircle className="text-green-500" size={32} />
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-yellow-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Pending</p>
              <p className="text-2xl font-bold text-gray-900">{stats.pending}</p>
            </div>
            <Clock className="text-yellow-500" size={32} />
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-purple-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Revenue</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrencyShort(stats.revenue)}</p>
              <button 
                onClick={fetchBillings}
                disabled={isLoading}
                className="text-xs text-gray-500 hover:text-gray-700 flex items-center mt-1"
              >
                <RefreshCw size={12} className={`mr-1 ${isLoading ? 'animate-spin' : ''}`} />
                {isLoading ? 'Loading...' : 'Refresh'}
              </button>
            </div>
            <DollarSign className="text-purple-500" size={32} />
          </div>
        </div>
      </div>
    );
  };

  // Mobile Header Component with green theme
  const MobileHeader = () => (
    <div className="lg:hidden bg-white shadow-sm border-b border-gray-200 px-4 py-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-green-500"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <div>
            <h1 className="text-lg font-semibold text-gray-900">Billing Management</h1>
            <p className="text-xs text-gray-600">{currentUser.role}</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {error && (
            <button
              onClick={handleRetry}
              className="bg-green-600 text-white px-3 py-1 rounded-md hover:bg-green-700 transition text-xs"
            >
              Retry
            </button>
          )}
          <div className="h-8 w-8 bg-green-600 rounded-full flex items-center justify-center">
            <span className="text-white text-sm font-medium">
              {currentUser.name.charAt(0).toUpperCase()}
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  // Sidebar Overlay for Mobile
  const SidebarOverlay = () => (
    <>
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </>
  );

  // Mobile Billing Card Component with green theme
  const MobileBillingCard = ({ billing }) => (
    <div className="bg-white rounded-lg shadow border border-gray-200 p-4 mb-4 hover:shadow-md transition-shadow">
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1 min-w-0">
          <button
            onClick={() => openDetailModal(billing)}
            className="text-sm font-medium text-green-600 hover:text-green-800 truncate block text-left"
          >
            {billing.invoiceNumber || billing._id}
          </button>
          <div className="flex items-center space-x-1 text-sm text-gray-600 mb-1">
            <User size={12} />
            <span className="truncate">{billing.customer?.fullName || 'N/A'}</span>
          </div>
          <div className="flex items-center space-x-1 text-sm text-gray-600 mb-2">
            <Calendar size={12} />
            <span>Period: {billing.billing?.period || 'N/A'}</span>
          </div>
          
          <div className="flex flex-wrap gap-2 mb-2">
            <span className={`inline-flex items-center space-x-1 px-2 py-1 text-xs font-semibold rounded-full border ${getStatusColor(billing.payment?.status)}`}>
              {getStatusIcon(billing.payment?.status)}
              <span>{getStatusLabel(billing.payment?.status)}</span>
            </span>
          </div>
          
          <div className="flex justify-between items-center text-sm">
            <div>
              <div className="text-gray-500">Amount:</div>
              <div className="font-medium">{formatCurrencyShort(billing.pricing?.totalAmount || 0)}</div>
            </div>
            <div className="text-right">
              <div className="text-gray-500">Due Date:</div>
              <div className="text-xs text-gray-600">{formatDate(billing.billing?.dueDate)}</div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => openUpdateModal(billing)}
          className="flex-1 min-w-0 text-green-600 hover:text-green-900 text-xs bg-green-100 px-3 py-2 rounded-md hover:bg-green-200 transition flex items-center justify-center space-x-1"
        >
          <Edit size={12} />
          <span>Update</span>
        </button>
        <button
          onClick={() => openDetailModal(billing)}
          className="flex-1 min-w-0 text-blue-600 hover:text-blue-900 text-xs bg-blue-100 px-3 py-2 rounded-md hover:bg-blue-200 transition flex items-center justify-center space-x-1"
        >
          <Eye size={12} />
          <span>Detail</span>
        </button>
        <button
          onClick={() => openDeleteModal(billing)}
          className="flex-1 min-w-0 text-red-600 hover:text-red-900 text-xs bg-red-100 px-3 py-2 rounded-md hover:bg-red-200 transition flex items-center justify-center space-x-1"
        >
          <Trash2 size={12} />
          <span>Hapus</span>
        </button>
      </div>
    </div>
  );

  // Desktop Table Component with green theme
  const DesktopTable = () => (
    <div className="bg-white rounded-lg shadow overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Period</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due Date</th>
            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Aksi</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {billings.map((billing) => (
            <tr key={billing._id} className="hover:bg-gray-50 transition-colors">
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600 cursor-pointer hover:text-green-800"
                  onClick={() => openDetailModal(billing)}>
                <div className="flex items-center space-x-2">
                  <FileText size={16} className="text-gray-400" />
                  <span>{billing.invoiceNumber || billing._id}</span>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                <div className="flex items-center space-x-2">
                  <User size={14} className="text-gray-400" />
                  <span>{billing.customer?.fullName || 'N/A'}</span>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 capitalize">
                <div className="flex items-center space-x-2">
                  <Calendar size={14} className="text-gray-400" />
                  <span>{billing.billing?.period || 'N/A'}</span>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                <div className="flex items-center space-x-2">
                  <DollarSign size={14} className="text-gray-400" />
                  <span>{formatCurrency(billing.pricing?.totalAmount || 0)}</span>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center space-x-2">
                  <span className={`inline-flex items-center space-x-1 px-2 py-1 text-xs font-semibold rounded-full border ${getStatusColor(billing.payment?.status)}`}>
                    {getStatusIcon(billing.payment?.status)}
                    <span>{getStatusLabel(billing.payment?.status)}</span>
                  </span>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                <div className="flex items-center space-x-2">
                  <Calendar size={14} className="text-gray-400" />
                  <span>{formatDate(billing.billing?.dueDate)}</span>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                <div className="flex space-x-1 justify-center">
                  <button
                    onClick={() => openUpdateModal(billing)}
                    className="text-green-600 hover:text-green-900 text-xs bg-green-100 px-3 py-2 rounded-md hover:bg-green-200 transition flex items-center space-x-1"
                    title="Update Billing"
                  >
                    <Edit size={12} />
                    <span>Update</span>
                  </button>
                  <button
                    onClick={() => openDetailModal(billing)}
                    className="text-blue-600 hover:text-blue-900 text-xs bg-blue-100 px-3 py-2 rounded-md hover:bg-blue-200 transition flex items-center space-x-1"
                    title="View Details"
                  >
                    <Eye size={12} />
                    <span>Detail</span>
                  </button>
                  <button
                    onClick={() => openDeleteModal(billing)}
                    className="text-red-600 hover:text-red-900 text-xs bg-red-100 px-3 py-2 rounded-md hover:bg-red-200 transition flex items-center space-x-1"
                    title="Delete Billing"
                  >
                    <Trash2 size={12} />
                    <span>Hapus</span>
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <RequireAdminRole user={user} loading={loading}>
      <div className="flex h-screen bg-gray-50 overflow-hidden">
        <SidebarOverlay />
        
        {/* Sidebar - Mobile responsive */}
        <div className={`
          fixed inset-y-0 left-0 z-50 w-64 transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}>
          <SidebarHeader 
            activeTab={activeTab} 
            setActiveTab={setActiveTab} 
            currentUser={currentUser}
            onClose={() => setSidebarOpen(false)}
          />
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <MobileHeader />

          <main className="flex-1 overflow-auto p-3 sm:p-6">
            {/* Main Panel dengan green theme */}
            <div className="bg-white rounded-lg shadow-xl">
              {/* Header dengan gradient hijau */}
              <div className="bg-gradient-to-r from-green-600 to-green-700 text-white p-4 rounded-t-lg flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center">
                    <DollarSign className="text-green-600" size={24} />
                  </div>
                  <div>
                    <h1 className="font-bold text-xl">Billing Management</h1>
                    <div className="flex items-center space-x-2 text-sm">
                      <span className="text-green-100">
                        {billings.length} billings ditemukan • Page {page} dari {totalPages}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={fetchBillings}
                    disabled={isLoading}
                    className="hover:bg-green-700 p-2 rounded transition-colors"
                    title="Refresh"
                  >
                    <RefreshCw size={20} className={isLoading ? 'animate-spin' : ''} />
                  </button>
                  <button
                    onClick={() => {/* Export functionality */}}
                    className="bg-white text-green-600 hover:bg-green-50 px-4 py-2 rounded-lg transition-colors flex items-center space-x-2"
                  >
                    <Download size={20} />
                    <span>Export</span>
                  </button>
                </div>
              </div>

              {/* Stats Cards */}
              <div className="p-4 border-b border-gray-200">
                <StatsCards />
              </div>

              {/* Filters and Search */}
              <div className="p-4 border-b border-gray-200">
                <div className="flex flex-col lg:flex-row gap-4">
                  {/* Search */}
                  <form onSubmit={handleSearchSubmit} className="flex-1">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                      <input
                        type="text"
                        placeholder="Cari berdasarkan invoice atau customer..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                    </div>
                  </form>

                  {/* Filter Toggle untuk Mobile */}
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className="lg:hidden flex items-center justify-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    <Filter size={20} />
                    <span>Filter</span>
                    {showFilters ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </button>

                  {/* Filters */}
                  <div className={`flex flex-col lg:flex-row gap-2 ${showFilters ? 'block' : 'hidden lg:flex'}`}>
                    <select
                      value={statusFilter}
                      onChange={(e) => {
                        setStatusFilter(e.target.value);
                        setPage(1);
                      }}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    >
                      {STATUS_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    <select
                      value={periodFilter}
                      onChange={(e) => {
                        setPeriodFilter(e.target.value);
                        setPage(1);
                      }}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    >
                      {PERIOD_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Error Display */}
              {error && (
                <div className="mx-4 mt-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <span>⚠️</span>
                      <span>{error}</span>
                    </div>
                    <button
                      onClick={handleRetry}
                      className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700"
                    >
                      Coba Lagi
                    </button>
                  </div>
                </div>
              )}

              {/* Billings Content */}
              <div className="p-4">
                {isLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="flex flex-col items-center">
                      <RefreshCw size={32} className="animate-spin text-green-600 mb-2" />
                      <span className="text-gray-500">Loading billings...</span>
                    </div>
                  </div>
                ) : error ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="text-center max-w-md mx-auto">
                      <div className="text-red-600 mb-4">
                        <span className="text-4xl">⚠️</span>
                      </div>
                      <h3 className="text-lg font-medium text-gray-900 mb-2">Failed to Load Billings</h3>
                      <p className="text-gray-600 mb-4">{error}</p>
                      <button
                        onClick={handleRetry}
                        className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition"
                      >
                        Try Again
                      </button>
                    </div>
                  </div>
                ) : billings.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="text-gray-400 mb-4">
                      <FileText size={64} className="mx-auto opacity-30" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No Billings Found</h3>
                    <p className="text-gray-600">
                      {search || statusFilter || periodFilter ? 'Try adjusting your search filters' : 'No billings have been created yet'}
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Mobile Cards */}
                    <div className="sm:hidden">
                      {billings.map((billing) => (
                        <MobileBillingCard key={billing._id} billing={billing} />
                      ))}
                    </div>

                    {/* Desktop Table */}
                    <div className="hidden sm:block">
                      <DesktopTable />
                    </div>
                  </>
                )}

                {/* Pagination */}
                {!error && !isLoading && billings.length > 0 && (
                  <div className="flex flex-col sm:flex-row justify-between items-center mt-6 space-y-3 sm:space-y-0">
                    <button
                      onClick={() => goToPage(page - 1)}
                      disabled={page === 1}
                      className="w-full sm:w-auto px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Sebelumnya
                    </button>
                    <span className="text-sm text-gray-700">
                      Halaman {page} dari {totalPages} ({billings.length} billings)
                    </span>
                    <button
                      onClick={() => goToPage(page + 1)}
                      disabled={page === totalPages}
                      className="w-full sm:w-auto px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Berikutnya
                    </button>
                  </div>
                )}
              </div>
            </div>
          </main>
        </div>

        {/* Update Billing Modal */}
        {showUpdateModal && selectedBilling && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-auto max-h-[90vh] overflow-y-auto">
              <div className="p-4 sm:p-5">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Update Billing Status</h3>
                  <button
                    onClick={() => setShowUpdateModal(false)}
                    className="text-gray-400 hover:text-gray-600 text-xl"
                  >
                    ×
                  </button>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Payment Status</label>
                    <select
                      value={billingForm.status}
                      onChange={(e) => setBillingForm({...billingForm, status: e.target.value})}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                    >
                      <option value="">Select Status</option>
                      {STATUS_OPTIONS.slice(1).map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  {billingForm.status === 'paid' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Payment Date</label>
                        <input
                          type="date"
                          value={billingForm.paymentDate}
                          onChange={(e) => setBillingForm({...billingForm, paymentDate: e.target.value})}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Payment Method</label>
                        <select
                          value={billingForm.paymentMethod}
                          onChange={(e) => setBillingForm({...billingForm, paymentMethod: e.target.value})}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                        >
                          <option value="">Select Method</option>
                          <option value="bank_transfer">Bank Transfer</option>
                          <option value="credit_card">Credit Card</option>
                          <option value="cash">Cash</option>
                          <option value="e_wallet">E-Wallet</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                    </>
                  )}
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                    <textarea
                      value={billingForm.notes}
                      onChange={(e) => setBillingForm({...billingForm, notes: e.target.value})}
                      rows={3}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                      placeholder="Optional notes..."
                    />
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 mt-6">
                  <button
                    onClick={() => setShowUpdateModal(false)}
                    className="w-full sm:w-auto px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 text-sm sm:text-base"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUpdateBilling}
                    className="w-full sm:w-auto px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm sm:text-base"
                  >
                    Update
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Billing Modal */}
        {showDeleteModal && selectedBilling && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-auto">
              <div className="p-4 sm:p-5">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Delete Billing</h3>
                <p className="text-sm text-gray-700 mb-6">
                  Apakah Anda yakin ingin menghapus billing <strong>{selectedBilling.invoiceNumber || selectedBilling._id}</strong>? 
                  Tindakan ini tidak dapat dibatalkan.
                </p>
                <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3">
                  <button
                    onClick={() => setShowDeleteModal(false)}
                    className="w-full sm:w-auto px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 text-sm sm:text-base"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteBilling}
                    className="w-full sm:w-auto px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm sm:text-base"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Billing Detail Modal */}
        {showDetailModal && selectedBilling && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-4xl mx-auto max-h-[90vh] overflow-y-auto">
              <div className="p-4 sm:p-5">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Billing Details</h3>
                  <button
                    onClick={() => setShowDetailModal(false)}
                    className="text-gray-400 hover:text-gray-600 text-xl"
                  >
                    ×
                  </button>
                </div>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                  {/* Basic Info */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                      <FileText size={18} />
                      <span>Informasi Dasar</span>
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div><span className="font-medium">Invoice Number:</span> {selectedBilling.invoiceNumber || 'N/A'}</div>
                      <div><span className="font-medium">ID:</span> {selectedBilling._id}</div>
                      <div className="flex items-center space-x-2">
                        <User size={14} className="text-gray-400" />
                        <span><span className="font-medium">Customer:</span> {selectedBilling.customer?.fullName || 'N/A'}</span>
                      </div>
                      <div><span className="font-medium">Email:</span> {selectedBilling.customer?.email || 'N/A'}</div>
                      <div><span className="font-medium">Phone:</span> {selectedBilling.customer?.phone || 'N/A'}</div>
                    </div>
                  </div>

                  {/* Billing Info */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                      <Calendar size={18} />
                      <span>Informasi Billing</span>
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div><span className="font-medium">Period:</span> {selectedBilling.billing?.period || 'N/A'}</div>
                      <div><span className="font-medium">Billing Date:</span> {formatDate(selectedBilling.billing?.billingDate)}</div>
                      <div><span className="font-medium">Due Date:</span> {formatDate(selectedBilling.billing?.dueDate)}</div>
                      <div><span className="font-medium">Service Type:</span> {selectedBilling.billing?.serviceType || 'N/A'}</div>
                      {selectedBilling.billing?.notes && (
                        <div><span className="font-medium">Notes:</span> {selectedBilling.billing.notes}</div>
                      )}
                    </div>
                  </div>

                  {/* Pricing Info */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                      <DollarSign size={18} />
                      <span>Detail Harga</span>
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div><span className="font-medium">Base Amount:</span> {formatCurrency(selectedBilling.pricing?.baseAmount || 0)}</div>
                      <div><span className="font-medium">Tax Amount:</span> {formatCurrency(selectedBilling.pricing?.taxAmount || 0)}</div>
                      <div><span className="font-medium">Discount:</span> {formatCurrency(selectedBilling.pricing?.discountAmount || 0)}</div>
                      <div className="border-t pt-1 mt-2">
                        <span className="font-medium">Total Amount:</span> 
                        <span className="font-bold text-lg ml-2">{formatCurrency(selectedBilling.pricing?.totalAmount || 0)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Payment Info */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                      <CreditCard size={18} />
                      <span>Informasi Pembayaran</span>
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center space-x-2">
                        <span className="font-medium">Status:</span>
                        <span className={`inline-flex items-center space-x-1 px-2 py-1 text-xs font-semibold rounded-full border ${getStatusColor(selectedBilling.payment?.status)}`}>
                          {getStatusIcon(selectedBilling.payment?.status)}
                          <span>{getStatusLabel(selectedBilling.payment?.status)}</span>
                        </span>
                      </div>
                      <div><span className="font-medium">Method:</span> {selectedBilling.payment?.method || 'N/A'}</div>
                      <div><span className="font-medium">Paid At:</span> {formatDateTime(selectedBilling.payment?.paidAt)}</div>
                      <div><span className="font-medium">Payment ID:</span> {selectedBilling.payment?.paymentId || 'N/A'}</div>
                      <div><span className="font-medium">Amount Paid:</span> {formatCurrency(selectedBilling.payment?.amountPaid || 0)}</div>
                    </div>
                  </div>

                  {/* Order/Package Info */}
                  {selectedBilling.order && (
                    <div className="bg-gray-50 p-4 rounded-lg lg:col-span-2">
                      <h4 className="font-semibold text-gray-900 mb-3">Order Terkait</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                        <div>
                          <div><span className="font-medium">Order Number:</span> {selectedBilling.order.orderNumber || 'N/A'}</div>
                          <div><span className="font-medium">Package:</span> {selectedBilling.order.packageSnapshot?.name || 'N/A'}</div>
                          <div><span className="font-medium">Speed:</span> {selectedBilling.order.packageSnapshot?.speed || 'N/A'}</div>
                        </div>
                        <div>
                          <div><span className="font-medium">Order Status:</span> {selectedBilling.order.status || 'N/A'}</div>
                          <div><span className="font-medium">Installation Date:</span> {formatDate(selectedBilling.order.installation?.scheduledDate)}</div>
                          <div><span className="font-medium">Created:</span> {formatDate(selectedBilling.order.createdAt)}</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Timestamps */}
                  <div className="bg-gray-50 p-4 rounded-lg lg:col-span-2">
                    <h4 className="font-semibold text-gray-900 mb-3">Informasi Sistem</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                      <div><span className="font-medium">Dibuat:</span> {formatDateTime(selectedBilling.createdAt)}</div>
                      <div><span className="font-medium">Terakhir Update:</span> {formatDateTime(selectedBilling.updatedAt)}</div>
                      <div><span className="font-medium">Version:</span> {selectedBilling.__v || 0}</div>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 mt-6">
                  <button
                    onClick={() => setShowDetailModal(false)}
                    className="w-full sm:w-auto px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 text-sm sm:text-base"
                  >
                    Close
                  </button>
                  <button
                    onClick={() => {
                      setShowDetailModal(false);
                      openUpdateModal(selectedBilling);
                    }}
                    className="w-full sm:w-auto px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm sm:text-base"
                  >
                    Update Billing
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </RequireAdminRole>
  );
};

export default AdminBillingPage;